import { useEffect, useState } from 'react'
import { ScrollView, StyleSheet, Text, View } from 'react-native'
import { BandCheckResult } from '../components/BandCheckResult'
import { BandRequiredForm } from '../components/BandRequiredForm'
import { PostcodeForm } from '../components/PostcodeForm'
import { toFriendlyError } from '../lib/error-message'
import { colors, fontFamily } from '../tokens'
import { trpc } from '../trpc'

type RunOutput = ReturnType<typeof trpc.bandCheck.run.useMutation>['data']

type Props = {
  // When set (from the ?bandCheck=<id> email link), hydrate the stored
  // verdict on mount instead of starting from the empty postcode form.
  initialBandCheckId?: string
}

export const BandCheckScreen = ({ initialBandCheckId }: Props = {}) => {
  const [result, setResult] = useState<RunOutput | null>(null)
  const runCheck = trpc.bandCheck.run.useMutation({
    onSuccess: (data) => setResult(data),
  })
  const hydration = trpc.bandCheck.getById.useQuery(
    { id: initialBandCheckId ?? '' },
    { enabled: !!initialBandCheckId && result == null, retry: false },
  )
  useEffect(() => {
    if (hydration.data && result == null) setResult(hydration.data)
  }, [hydration.data, result])

  const reset = () => {
    setResult(null)
    runCheck.reset()
  }

  const hydrating = !!initialBandCheckId && hydration.isLoading && result == null
  const hydrationFailed = !!initialBandCheckId && !!hydration.error && result == null

  return (
    <ScrollView contentContainerStyle={styles.scroll}>
      <View accessibilityLiveRegion="polite" aria-label="Band check" style={styles.page}>
        {result?.kind === 'verdict' ? (
          <BandCheckResult result={result} onCheckAnother={reset} />
        ) : hydrating ? (
          <Text style={styles.statusText}>Loading your report…</Text>
        ) : result?.kind === 'band-required' ? (
          <BandRequiredForm
            postcode={result.postcode}
            houseNumber={result.houseNumber}
            reason={result.reason}
            validBands={result.validBands}
            bandSuggestion={result.bandSuggestion}
            chargeByBand={result.chargeByBand}
            localAuthority={result.localAuthority}
            onSubmit={(band) =>
              runCheck.mutate({
                postcode: result.postcode,
                houseNumber: result.houseNumber,
                userBand: band as 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H' | 'I',
                surface: 'app',
              })
            }
            isPending={runCheck.isPending}
            errorMessage={runCheck.error?.message}
          />
        ) : (
          <>
            {hydrationFailed ? (
              <Text style={styles.statusText}>
                We couldn't find that report. It may have expired — run a fresh check below.
              </Text>
            ) : null}
            <PostcodeForm
              onSubmit={(input) => runCheck.mutate({ ...input, surface: 'app' })}
              isPending={runCheck.isPending}
              errorMessage={runCheck.error ? toFriendlyError(runCheck.error).message : undefined}
            />
          </>
        )}
      </View>
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  scroll: { flexGrow: 1, backgroundColor: colors.bg },
  page: { paddingHorizontal: 24, paddingVertical: 48, alignItems: 'center' },
  statusText: {
    fontSize: 16,
    color: colors.muted,
    marginBottom: 16,
    textAlign: 'center',
    fontFamily: fontFamily.body,
  },
})
